import { useEffect, useMemo, useRef } from 'react';

import { useQuery } from '@tanstack/react-query';
import { AgGridReact } from 'ag-grid-react';
import { useSearchParams } from 'react-router-dom';

import { WithLocalStorage } from '@/components';
import { SearchableSelect } from '@/components/common/searchable-select';
import usePageTitle from '@/components/hooks/usePageTitle';

import { auditApi } from '../api';
import { type DateRangePreset, Toolbar } from '../components/Toolbar';
import { cutoffIsoFor } from '../util/dateRange';
import { AuditFeedGrid } from './AuditFeedGrid';
import { useAuditFeedDatasource } from './useAuditFeedDatasource';

/** `/audit`: recent audit activity across records, filterable by entity. */
export function AuditHomePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const objectType = searchParams.get('type') ?? '';
  const search = searchParams.get('q') ?? '';
  const dateRange = (searchParams.get('range') ?? '7d') as DateRangePreset;

  const setParam = (key: string, value: string, fallback = '') => {
    const next = new URLSearchParams(searchParams);
    if (value && value !== fallback) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: true });
  };

  const { setTitle, clearTitle } = usePageTitle();
  useEffect(() => {
    setTitle('Audit Log', 'Audit Log');
    return clearTitle;
  }, [setTitle, clearTitle]);

  const { data: entityTypes = [], isLoading: entityTypesLoading } = useQuery({
    queryKey: ['audit', 'entity-types'],
    queryFn: ({ signal }) => auditApi.fetchAuditEntityTypes('en-US', signal),
    staleTime: 60 * 60 * 1000,
  });

  const entityOptions = useMemo(
    () =>
      entityTypes.map((e) => ({ value: e.objectType, label: e.displayName })),
    [entityTypes],
  );

  const grid = useRef<AgGridReact>(null);
  const dataSource = useAuditFeedDatasource({
    objectType,
    q: search,
    startDate: cutoffIsoFor(dateRange) ?? '',
  });

  useEffect(() => {
    grid.current?.api?.refreshServerSide({ purge: true });
  }, [objectType, search, dateRange]);

  return (
    <WithLocalStorage storageKey="audit-home-grid">
      <div className="flex h-full flex-col">
        <Toolbar
          leading={
            <SearchableSelect
              className="w-64"
              options={entityOptions}
              value={objectType}
              onChange={(value) => setParam('type', value)}
              placeholder={
                entityTypesLoading ? 'Loading entities...' : 'All entities'
              }
              emptyMessage="No matching entities"
            />
          }
          search={search}
          onSearchChange={(value) => setParam('q', value)}
          dateRange={dateRange}
          onDateRangeChange={(value) => setParam('range', value, '7d')}
        />
        <div className="min-h-0 flex-1 pt-3">
          <AuditFeedGrid grid={grid} dataSource={dataSource} />
        </div>
      </div>
    </WithLocalStorage>
  );
}
